// change support-requests: the context "Contact support" attaches automatically. The api client wrapper (api.ts) records
// the last API request ids and errors here, in memory only (never stored, never sent anywhere else); the support form
// shows exactly what it will send. Only paths travel — no query strings, tokens, cookies or request bodies.
import type { SupportContext, SupportContextError } from '@viewstream/api-client';
import { telemetryBuffer } from './telemetry';

const MAX = 10;
const ids: string[] = [];
const errors: SupportContextError[] = [];

function push<T>(list: T[], v: T) {
  list.push(v);
  if (list.length > MAX) list.shift();
}

/** Path of a request URL without query string or fragment. */
function pathOf(url: string): string {
  try { return new URL(url, window.location.origin).pathname; } catch { return ''; }
}

/** Records one API response (called by the client middleware for every /v1 call). */
export async function recordApiResponse(request: Request, response: Response): Promise<void> {
  const path = pathOf(request.url);
  if (!path.startsWith('/v1/') || path.startsWith('/v1/support/') || path === '/v1/events/stream') return;
  const rid = response.headers.get('x-request-id');
  if (rid) push(ids, rid);
  if (response.ok) return;
  let type: string | undefined;
  let message: string | undefined;
  try {
    const p = (await response.clone().json()) as { type?: unknown; title?: unknown; detail?: unknown };
    if (typeof p.type === 'string') type = p.type.replace(/^https:\/\/viewstream\.co\.il\/problems\//, '');
    message = [p.title, p.detail].filter((x): x is string => typeof x === 'string' && x !== '').join(' — ').slice(0, 500);
  } catch { /* not JSON */ }
  push(errors, { at: new Date().toISOString(), method: request.method, path, status: response.status, type, message, request_id: rid ?? undefined });
}

/** Records an error the app showed (the error page "Something went wrong"). */
export function recordUiError(message: string, requestId?: string | null) {
  push(errors, { at: new Date().toISOString(), method: 'UI', path: window.location.pathname, type: 'ui_error', message: message.slice(0, 500), request_id: requestId ?? undefined });
}

const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';

/** The context for a request opened from `from` (a Studio path; the current page when empty). */
export function collectSupportContext(from: string | undefined, locale: string, tz: string): SupportContext {
  const page = (from && from.startsWith('/') ? from : window.location.pathname).split(/[?#]/)[0] ?? '';
  const ctx: SupportContext = {
    page_path: page.slice(0, 500),
    studio_version: `${__VS_VERSION__}+${__RELEASE__.slice(0, 8)}`,
    user_agent: navigator.userAgent.slice(0, 500),
    time_zone: tz || Intl.DateTimeFormat().resolvedOptions().timeZone,
    locale,
  };
  const ch = new RegExp(`/live/(${UUID})`, 'i').exec(page);
  if (ch?.[1]) ctx.channel_id = ch[1];
  const asset = new RegExp(`/library/(${UUID})`, 'i').exec(page);
  if (asset?.[1]) ctx.asset_id = asset[1];
  if (ids.length) ctx.request_ids = [...ids].reverse();
  const errs = [...errors];
  // front-end errors the telemetry buffer saw that the error page did not record (message only)
  for (const ev of telemetryBuffer()) {
    if (ev.kind !== 'error' || errs.length >= MAX) continue;
    const msg = (ev.data as { message?: unknown } | null)?.message;
    if (typeof msg === 'string' && !errs.some((e) => e.message === msg.slice(0, 500))) errs.push({ at: ev.at, method: 'UI', path: ev.route.split(/[?#]/)[0], type: 'ui_error', message: msg.slice(0, 500) });
  }
  if (errs.length) ctx.errors = errs.slice(-MAX).reverse();
  return ctx;
}
